import { Tool } from '@goat-sdk/core';
import { EVMWalletClient } from '@goat-sdk/wallet-evm';
import { erc20Abi, formatUnits } from 'viem';

import WagmiWalletClient from 'mode-shared/clients/WagmiWalletClient';
import { VOTING_ESCROW_ABI } from './abi';
import {
  CLOCK_ADDRESSES,
  DECIMALS,
  STAKE_TOKEN_ADDRESS,
  VOTER_ADDRESSES,
  VOTING_ESCROW_ADDRESSES,
  WARMUP_PERIOD,
} from './constants';
import {
  ExecuteGovernanceStakeParameters,
  GetGaugeInfoParameters,
  GetModeGovernanceInfoParameters,
  VoteOnGaugesParameters,
} from './parameters';
import { Logger } from 'mode-shared/types/logger';
import { CommonParameters } from 'mode-shared/types/common';
import { GAUGE_VOTER_ABI } from './abi/gaugeVoter';
import { CLOCK_ABI } from './abi/clock';
import { NoParameters } from 'mode-shared/tools/noParameters';

// Interface for gauge information
interface GaugeInfo {
  address: string;
  active: boolean;
  created: number;
  metadataURI: string;
  totalVotes: string;
  name: string;
  description: string;
  logo: string;
}

export class ModeGovernanceService {
  private readonly _logger: Logger;
  public static displayName = 'ModeGovernanceService';

  public constructor({ logger }: CommonParameters) {
    this._logger = logger;
  }

  /**
   * private methods
   */

  private async _address(walletClient: EVMWalletClient): Promise<string> {
    if ((walletClient as WagmiWalletClient).isWagmi) {
      return await (walletClient as WagmiWalletClient).getAddressAsync();
    }

    return walletClient.getAddress();
  }

  /**
   * public methods
   */

  // Staking

  @Tool({
    description: 'Executes the staking transaction of MODE and/or BPT tokens for governance.',
  })
  async executeGovernanceStake(walletClient: EVMWalletClient, parameters: ExecuteGovernanceStakeParameters) {
    const __logPrefix = `${ModeGovernanceService.displayName}#executeGovernanceStake`;

    try {
      const tokenAddress = STAKE_TOKEN_ADDRESS[parameters.stakeType];
      const escrowAddress = VOTING_ESCROW_ADDRESSES[parameters.stakeType];
      const amount = BigInt(parameters.amount ?? '0');

      const currentAllowance = await walletClient.read({
        address: tokenAddress,
        abi: erc20Abi,
        functionName: 'allowance',
        args: [await this._address(walletClient), escrowAddress],
      });

      if (!currentAllowance || currentAllowance.value === undefined) {
        return 'Allowance value is undefined';
      }
      const allowance = BigInt(currentAllowance.value as string);

      if (allowance < amount) {
        await walletClient.sendTransaction({
          to: tokenAddress,
          abi: erc20Abi,
          functionName: 'approve',
          args: [escrowAddress, amount],
        });
      }

      const stakeTx = await walletClient.sendTransaction({
        to: escrowAddress,
        abi: VOTING_ESCROW_ABI,
        functionName: 'createLock',
        args: [amount],
      });

      return {
        amount: amount.toString(),
        allowance: allowance.toString(),
        stakeHash: stakeTx,
      };
    } catch (error) {
      this._logger.error(`${__logPrefix}:`, error);

      return error instanceof Error ? error.message : 'Unknown error';
    }
  }

  // TODO: unstack

  @Tool({
    description: 'Retrieves detailed MODE governance information, staked tokens, voting power, votes and token IDs.',
  })
  async getModeGovernanceInfo(walletClient: EVMWalletClient, parameters: GetModeGovernanceInfoParameters) {
    const __logPrefix = `${ModeGovernanceService.displayName}#getModeGovernanceInfo`;

    const contracts = [];
    if (parameters.voterType === 'all') {
      contracts.push(
        [VOTING_ESCROW_ADDRESSES.veMODE, VOTER_ADDRESSES.veMODE],
        [VOTING_ESCROW_ADDRESSES.veBPT, VOTER_ADDRESSES.veBPT]
      );
    } else {
      contracts.push([VOTING_ESCROW_ADDRESSES[parameters.voterType], VOTER_ADDRESSES[parameters.voterType]]);
    }

    const userAddress = await this._address(walletClient);

    try {
      const contractVotesPromises = Promise.all(
        contracts.map(async ([escrowAddress, voterAddress]) => {
          const ownedTokens = (await walletClient.read({
            address: escrowAddress,
            abi: VOTING_ESCROW_ABI,
            functionName: 'ownedTokens',
            args: [userAddress],
          })) as { value: bigint[] };

          const tokenIds = ownedTokens.value;

          if (tokenIds.length === 0) {
            return {
              totalLocked: BigInt(0),
              totalInWarmup: BigInt(0),
              votingPower: BigInt(0),
              gaugesVote: [
                {
                  id: BigInt(0),
                  gauge: '',
                  usedVotingPower: BigInt(0),
                },
              ],
            };
          }

          const tokenVotes = await Promise.all(
            tokenIds.map(async (id) => {
              const { amount, start } = (
                (await walletClient.read({
                  address: escrowAddress,
                  abi: VOTING_ESCROW_ABI,
                  functionName: 'locked',
                  args: [id],
                })) as { value: { amount: bigint; start: number } }
              ).value;

              // start is the timestamp when the lock started
              const isWarmup = Date.now() / 1000 - start < WARMUP_PERIOD;

              const votedFor = (
                (await walletClient.read({
                  address: voterAddress,
                  abi: GAUGE_VOTER_ABI,
                  functionName: 'gaugesVotedFor',
                  args: [id],
                })) as { value: string[] }
              ).value;

              const votes = await Promise.all(
                votedFor.map(async (gauge) => {
                  const usedVotingPower = (
                    (await walletClient.read({
                      address: voterAddress,
                      abi: GAUGE_VOTER_ABI,
                      functionName: 'votes',
                      args: [id, gauge],
                    })) as { value: bigint }
                  ).value;

                  return {
                    gauge,
                    usedVotingPower,
                  };
                })
              );

              return {
                amount,
                votes,
                isWarmup,
                warmupUntil: start + WARMUP_PERIOD,
              };
            })
          );

          const votingPower = (
            (await walletClient.read({
              address: escrowAddress,
              abi: VOTING_ESCROW_ABI,
              functionName: 'votingPowerForAccount',
              args: [userAddress],
            })) as { value: bigint }
          ).value;

          return {
            totalLocked: tokenVotes.reduce((sum, item) => sum + item.amount, BigInt(0)),
            totalInWarmup: tokenVotes.reduce((sum, item) => (item.isWarmup ? sum + item.amount : sum), BigInt(0)),
            votingPower,
            gaugesVote: tokenVotes.flatMap((item) => item.votes),
          };
        })
      );
      const [contractVotes, allGauges] = await Promise.all([
        contractVotesPromises,
        this.getAllGauges(walletClient, { voterType: parameters.voterType }),
      ]);

      if (allGauges instanceof String) {
        return allGauges;
      }

      const totalLocked = contractVotes.reduce((sum, item) => sum + item.totalLocked, BigInt(0));
      const totalInWarmup = contractVotes.reduce((sum, item) => sum + item.totalInWarmup, BigInt(0));
      const votingPower = contractVotes.reduce((sum, item) => sum + item.votingPower, BigInt(0));
      const votes = contractVotes.flatMap((item) => item.gaugesVote);

      // group by voting gauge
      const gaugesVote = votes.reduce(
        (acc, item) => {
          const gaugeName =
            (allGauges as GaugeInfo[]).find((gauge) => gauge.address === item.gauge)?.name || item.gauge;
          if (!acc[gaugeName]) {
            acc[gaugeName] = { totalVotingPower: BigInt(0), tokenIds: [] };
          }
          acc[gaugeName].totalVotingPower += item.usedVotingPower;
          return acc;
        },
        {} as Record<string, { totalVotingPower: bigint; tokenIds: string[] }>
      );

      return {
        stakedAmount: formatUnits(totalLocked, DECIMALS),
        totalInWarmup: formatUnits(totalInWarmup, DECIMALS),
        votingPower: this.formatVotingPower(votingPower),
        gaugesVote,
      };
    } catch (error) {
      this._logger.error(`${__logPrefix}:`, error);

      return error instanceof Error ? error.message : 'Unknown error';
    }
  }

  formatVotingPower(votingPower: bigint): string {
    // return XXK or XXM format
    const DECIMALS = BigInt(10 ** 18);
    if (votingPower >= DECIMALS * BigInt(1_000_000)) {
      return `${votingPower / (DECIMALS * BigInt(1_000_000))}M`;
    } else if (votingPower >= DECIMALS * BigInt(1_000)) {
      return `${votingPower / (DECIMALS * BigInt(1_000))}K`;
    } else {
      return votingPower.toString();
    }
  }

  // Voting

  @Tool({
    name: 'get_all_gauges_mode',
    description:
      'Get a list of all available voting gauges on Mode Network. Use veMODE for MODE token gauges or veBPT for Balancer Pool Token gauges.',
  })
  async getAllGauges(walletClient: EVMWalletClient, _: NoParameters): Promise<GaugeInfo[] | string> {
    try {
      const voterAddress = VOTER_ADDRESSES.veMODE;

      const gaugesResult = await walletClient.read({
        address: voterAddress,
        abi: GAUGE_VOTER_ABI,
        functionName: 'getAllGauges',
        args: [],
      });
      const gauges = (gaugesResult as { value: string[] }).value;

      const gaugesDetails = await Promise.all(
        gauges.map(async (gauge) => {
          return this.getGaugeInfo(walletClient, {
            voterType: 'veMODE',
            gaugeAddress: gauge,
          });
        })
      );

      return gaugesDetails.filter((gauge) => typeof gauge !== 'string');
    } catch (error) {
      return `Failed to get gauges: ${error instanceof Error ? error.message : 'Unknown error'}`;
    }
  }

  async fetchIpfsAsJson(metadataURI: string) {
    const response = await fetch(`https://ipfs.io/ipfs/${metadataURI.slice(7)}`);
    return (await response.json()) as Promise<{
      name: string;
      description: string;
      logo: string;
    }>;
  }

  // @Tool({
  //   name: 'get_gauge_info_mode',
  //   description: 'Get detailed information about a specific gauge',
  // })
  async getGaugeInfo(walletClient: EVMWalletClient, parameters: GetGaugeInfoParameters): Promise<GaugeInfo | string> {
    try {
      const gaugeDataResult = await walletClient.read({
        address: VOTER_ADDRESSES[parameters.voterType],
        abi: GAUGE_VOTER_ABI,
        functionName: 'getGauge',
        args: [parameters.gaugeAddress],
      });

      const gaugeData = (
        gaugeDataResult as {
          value: {
            active: boolean;
            created: bigint;
            metadataURI: string;
          };
        }
      ).value;

      const votesResult = await walletClient.read({
        address: VOTER_ADDRESSES[parameters.voterType],
        abi: GAUGE_VOTER_ABI,
        functionName: 'gaugeVotes',
        args: [parameters.gaugeAddress],
      });
      const votes = (votesResult as { value: bigint }).value;

      const metadata = await this.fetchIpfsAsJson(gaugeData.metadataURI);

      const gaugeInfo: GaugeInfo = {
        address: parameters.gaugeAddress,
        active: gaugeData.active,
        created: Number(gaugeData.created),
        metadataURI: gaugeData.metadataURI,
        totalVotes: votes.toString(),
        name: metadata.name,
        description: metadata.description,
        logo: metadata.logo,
      };

      return gaugeInfo;
    } catch (error) {
      return `Failed to get gauge info: ${error instanceof Error ? error.message : 'Unknown error'}`;
    }
  }

  @Tool({
    name: 'vote_is_active',
    description: 'Check if voting is currently active',
  })
  async voteIsActive(walletClient: EVMWalletClient, _: NoParameters): Promise<boolean | string> {
    try {
      // Check if voting is active
      const clockAddress = CLOCK_ADDRESSES.veMODE;

      const votingActiveResult = await walletClient.read({
        address: clockAddress,
        abi: CLOCK_ABI,
        functionName: 'votingActive',
        args: [],
      });
      const isVotingActive = (votingActiveResult as { value: boolean }).value;

      return isVotingActive;
    } catch (error) {
      return `Failed to check if vote is active: ${error instanceof Error ? error.message : 'Unknown error'}`;
    }
  }

  @Tool({
    name: 'vote_on_gauges_mode',
    description: 'Vote on multiple gauges using your veNFT voting power. The sum of weights must equal 100.',
  })
  async voteOnGauges(walletClient: EVMWalletClient, parameters: VoteOnGaugesParameters): Promise<string> {
    try {
      const userAddress = await this._address(walletClient);

      const contracts = [
        { escrow: VOTING_ESCROW_ADDRESSES.veMODE, voter: VOTER_ADDRESSES.veMODE, clock: CLOCK_ADDRESSES.veMODE },
        { escrow: VOTING_ESCROW_ADDRESSES.veBPT, voter: VOTER_ADDRESSES.veBPT, clock: CLOCK_ADDRESSES.veBPT },
      ];

      const messages = [];

      for (const { escrow, voter, clock } of contracts) {
        // Check if voting is active
        const votingActiveResult = await walletClient.read({
          address: clock,
          abi: CLOCK_ABI,
          functionName: 'votingActive',
          args: [],
        });
        const isVotingActive = (votingActiveResult as { value: boolean }).value;

        if (!isVotingActive) {
          throw new Error('Voting is not currently active');
        }

        const ownedTokens = (
          (await walletClient.read({
            address: escrow,
            abi: VOTING_ESCROW_ABI,
            functionName: 'ownedTokens',
            args: [userAddress],
          })) as { value: bigint[] }
        ).value;

        const votableTokens = (
          await Promise.all(
            ownedTokens.map(async (id) => {
              const { value } = await (walletClient.read({
                address: escrow,
                abi: VOTING_ESCROW_ABI,
                functionName: 'locked',
                args: [id],
              }) as Promise<{ value: { start: number } }>);
              // start is the timestamp when the lock started
              const isWarmup = Date.now() / 1000 - value.start < WARMUP_PERIOD;
              return { id, isVotable: !isWarmup };
            })
          )
        )
          .filter((item) => item.isVotable)
          .map((item) => item.id);

        if (votableTokens.length === 0) {
          continue;
        }

        // Validate gauges and prepare vote data
        const votes = parameters.votes.map((vote) => ({
          gauge: vote.gauge,
          weight: BigInt(vote.weight),
        }));

        // Sum of weights validation
        const totalWeight = votes.reduce((sum, vote) => sum + BigInt(vote.weight), BigInt(0));
        if (totalWeight !== BigInt(100)) {
          throw new Error('Total vote weight must equal 100');
        }

        // Execute vote transaction
        const txHash = await walletClient.sendTransaction({
          to: voter,
          abi: GAUGE_VOTER_ABI,
          functionName: 'voteMultiple',
          args: [votableTokens, votes],
        });

        messages.push(`Successfully voted. Transaction: ${txHash.hash}`);
      }

      return messages.join('\n');
    } catch (error) {
      return `Failed to vote on gauges: ${error instanceof Error ? error.message : 'Unknown error'}`;
    }
  }
}
